import React, { useState } from "react";
import { Form, Input, Modal, Upload, notification } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import documentService from "../services/documentService";
import { buildInvoiceUploadPayload } from "../services/uploadInvoiceUtils";

const MAX_FILE_SIZE = 14 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf", ".heic"];

const getExtension = (fileName = "") =>
  fileName.slice(fileName.lastIndexOf(".")).toLowerCase();

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const UploadInvoiceModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const beforeUpload = (file) => {
    if (!SUPPORTED_EXTENSIONS.includes(getExtension(file.name))) {
      notification.error({ message: t("invoiceFileTypeError") });
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE) {
      notification.error({ message: t("invoiceFileSizeError") });
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!fileList.length) {
      notification.error({ message: t("invoiceFileRequired") });
      return;
    }

    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const file = fileList[0].originFileObj || fileList[0];
      const fileBase64 = await toBase64(file);
      await documentService.uploadInvoice(
        buildInvoiceUploadPayload(file, fileBase64, values)
      );

      notification.success({ message: t("invoiceUploadSuccess") });
      form.resetFields();
      setFileList([]);
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({
        message: t("invoiceUploadError"),
        description: err?.message || t("genericError"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("uploadInvoice")}
      open={open}
      onCancel={resetAndClose}
      onOk={handleSubmit}
      okText={t("uploadInvoice")}
      cancelText={t("cancel")}
      confirmLoading={submitting}
      destroyOnClose
      maskClosable={!submitting}
      closable={!submitting}
      className="app-form-modal upload-invoice-modal"
      centered
      width={520}
    >
      <Form form={form} layout="vertical" disabled={submitting}>
        <Form.Item label={t("invoiceFile")} required>
          <Upload.Dragger
            accept=".jpg,.jpeg,.png,.pdf,.heic"
            beforeUpload={beforeUpload}
            fileList={fileList}
            maxCount={1}
            multiple={false}
            onChange={({ fileList: nextFileList }) =>
              setFileList(nextFileList.slice(-1))
            }
            onRemove={() => setFileList([])}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t("selectInvoiceFile")}</p>
            <p className="ant-upload-hint">{t("supportedInvoiceFiles")}</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item name="inbox_type" label={t("inboxType")}>
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item name="description" label={t("description")}>
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
        <Form.Item name="diagnosis" label={t("diagnosis")}>
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadInvoiceModal;
